"use client";

import { useEffect, useState } from "react";

/**
 * Conferme in stile AIMM al posto di window.confirm.
 * Uso: `if (!(await confirmUI({ message: "Eliminare…?", danger: true }))) return;`
 * L'host (<ConfirmHost/>) è montato una volta nel layout root.
 */

export interface ConfirmOptions {
  title?: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** rosso per le azioni distruttive */
  danger?: boolean;
}

let pendingResolve: ((v: boolean) => void) | null = null;
let hostSetOpts: ((o: ConfirmOptions | null) => void) | null = null;

export function confirmUI(opts: ConfirmOptions): Promise<boolean> {
  // fallback: host non montato (non dovrebbe succedere) → confirm nativo
  if (!hostSetOpts) return Promise.resolve(window.confirm(opts.message));
  // una conferma alla volta: l'eventuale precedente viene annullata
  pendingResolve?.(false);
  return new Promise((resolve) => {
    pendingResolve = resolve;
    hostSetOpts?.(opts);
  });
}

export function ConfirmHost() {
  const [opts, setOpts] = useState<ConfirmOptions | null>(null);

  useEffect(() => {
    hostSetOpts = setOpts;
    return () => {
      hostSetOpts = null;
    };
  }, []);

  useEffect(() => {
    if (!opts) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        close(false);
      }
      if (e.key === "Enter") {
        e.stopPropagation();
        close(true);
      }
    };
    // capture: vince sui gestori del player/pagine sottostanti
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [opts]);

  function close(v: boolean) {
    setOpts(null);
    pendingResolve?.(v);
    pendingResolve = null;
  }

  if (!opts) return null;
  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/70 p-4"
      onClick={() => close(false)}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        className="w-full max-w-sm rounded-xl border border-edge bg-panel p-4 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="mb-1.5 text-sm font-semibold text-white">
          {opts.title ?? (opts.danger ? "Conferma eliminazione" : "Conferma")}
        </h3>
        <p className="mb-4 whitespace-pre-line text-[13px] leading-relaxed text-ink-2">{opts.message}</p>
        <div className="flex justify-end gap-2">
          <button
            onClick={() => close(false)}
            className="rounded-md border border-edge px-3.5 py-1.5 text-xs text-ink-2 hover:border-edge-2 hover:text-ink"
          >
            {opts.cancelLabel ?? "Annulla"}
          </button>
          <button
            onClick={() => close(true)}
            autoFocus
            className={`rounded-md px-3.5 py-1.5 text-xs font-semibold ${
              opts.danger
                ? "bg-red-600 text-white hover:bg-red-500"
                : "bg-accent text-[#1b1b1f] hover:bg-accent-2"
            }`}
          >
            {opts.confirmLabel ?? (opts.danger ? "Elimina" : "Conferma")}
          </button>
        </div>
      </div>
    </div>
  );
}
